import React, { useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, ChevronDown, ChevronRight, ArrowUpRight, ArrowDownRight,
  CheckCircle2, XCircle, AlertTriangle, FileText, Activity, Clock,
} from 'lucide-react';
import { Button, Pill, Card, EmptyState, TrustChip } from '../../index';
import { Pagination, usePaginationState } from '../../../common/Pagination';
import { ProbationTracker } from '../ProbationTracker';
import { ScoreWaterfall } from '../ScoreWaterfall';
import { AuditHashIntegrity } from '../AuditHashIntegrity';
import { TimelineScrubber } from '../TimelineScrubber';
import {
  getAutonomyHistory, getPolicyAuditTrail, getAgentScoreTrends,
  getTrustProfile,
  // P0-12 Sprint 2 — External-mirror witness status pill below the
  // chain-integrity verifier.
  getLatestAuditWitness,
} from '../../../../services/api';

/**
 * TrustTimelineTab — V2 Trust Timeline (Phase A8 / G308).
 *
 * Full V1 feature parity:
 *   - Agent picker + time range + event type + decision filters
 *   - Probation Tracker (countdown + recovery path) when applicable
 *   - Score Waterfall chart (SVG, threshold lines)
 *   - Stats Summary Strip (6 cards: Total / Transitions / Enforcements / Blocked / Warned / Allowed)
 *   - Audit Hash Chain Integrity verify panel
 *   - Vertical timeline with paginated, expandable event cards
 *
 * Two data sources merged into one timeline:
 *   - Autonomy history (transitions): /trust/profiles/{id}/history
 *   - Policy audit trail (enforcements): /audit/policy-trail
 *
 * Events are intercalated by timestamp and rendered as a single
 * chronological feed so the operator sees the full history-of-trust
 * in one place.
 *
 * @param {Object} props
 * @param {Array} props.enriched   — scope-filtered agents
 * @param {Function} props.addToast
 */
// TS3 — Historical Trust Score windows. 90d is the default rolling-score
// window (TS1); 180d/365d/All let the operator look back across the full
// retained history (retention default 3y / 1095d, TS4). "All" pulls the
// whole hot store up to the retention horizon (older history is archived).
const TIME_RANGES = [
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: '7d',  label: 'Last 7 days',   hours: 168 },
  { key: '30d', label: 'Last 30 days',  hours: 720 },
  { key: '90d', label: 'Last 90 days',  hours: 2160 },
  { key: '180d', label: 'Last 180 days', hours: 4320 },
  { key: '365d', label: 'Last 365 days', hours: 8760 },
  { key: 'all',  label: 'All (retained)', hours: 26280 }, // 1095d / 3y retention horizon
];
const EVENT_TYPES = [
  { key: 'all',          label: 'All events' },
  { key: 'transition',   label: 'Autonomy transitions' },
  { key: 'enforcement',  label: 'Policy enforcements' },
];
const DECISIONS = [
  { key: 'all',     label: 'All decisions' },
  { key: 'BLOCKED', label: 'Blocked' },
  { key: 'WARNED',  label: 'Warned' },
  { key: 'ALLOWED', label: 'Allowed' },
  { key: 'AUDIT',   label: 'Audit' },
  { key: 'REDACTED',label: 'Redacted' },
];

/**
 * G327 — extractList: pull an array out of any apiFetch response.
 *
 * apiFetch wraps every successful response as { ok, data: <body> }.
 * Several common backend shapes then layer further wrapping:
 *
 *   Plain array:        ["a", "b"]
 *   apiFetch + array:   { ok, data: ["a", "b"] }
 *   Spring Pageable:    { ok, data: { content: [...], totalElements, ... } }
 *   Custom paged:       { ok, data: { items: [...], totalPages } }
 *   Domain wrappers:    { ok, data: { transitions: [...] } } or { events }, { points }
 *
 * The previous TrustTimelineTab normaliser only checked
 * `Array.isArray(r.data)` — when data was a Pageable object it
 * silently returned the empty fallback, hiding the entire history.
 */
function extractList(r) {
  if (!r) return [];
  if (Array.isArray(r)) return r;
  // The outer apiFetch wrapper
  const d = r.data;
  if (Array.isArray(d)) return d;
  // Try common collection field names at top level
  for (const k of ['items', 'content', 'results', 'transitions', 'events', 'points', 'profiles', 'history']) {
    if (Array.isArray(r[k])) return r[k];
  }
  // Then drill into the apiFetch-wrapped body
  if (d && typeof d === 'object') {
    for (const k of ['content', 'items', 'results', 'transitions', 'events', 'points', 'profiles', 'history']) {
      if (Array.isArray(d[k])) return d[k];
    }
  }
  return [];
}

export function TrustTimelineTab({ enriched, addToast, focusAgentId, onFocusAgent }) {
  // Initialize from the shared cross-tab focus so switching tabs preserves the
  // selected agent (falls back to the first agent on first visit).
  const [selectedAgentId, setSelectedAgentId] = useState(() => focusAgentId || enriched[0]?.id || '');
  const [timeRange, setTimeRange] = useState('90d'); // TS3 — match canonical 90d score window
  const [eventType, setEventType] = useState('all');
  const [decision, setDecision] = useState('all');
  const [profile, setProfile] = useState(null);
  const [transitions, setTransitions] = useState([]);
  const [enforcements, setEnforcements] = useState([]);
  const [scorePoints, setScorePoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const pg = usePaginationState(20);

  /* Re-pin to the first agent when scope changes. */
  useEffect(() => {
    if (!selectedAgentId && enriched.length > 0) {
      setSelectedAgentId(enriched[0].id);
    }
    if (selectedAgentId && !enriched.find((a) => a.id === selectedAgentId) && enriched.length > 0) {
      setSelectedAgentId(enriched[0].id);
    }
  }, [enriched, selectedAgentId]);

  /* Load all data for selected agent. Three parallel calls; the
   * waterfall takes score trends, the timeline merges transitions
   * and enforcements. */
  const load = async () => {
    if (!selectedAgentId) return;
    setLoading(true);
    const hours = (TIME_RANGES.find((r) => r.key === timeRange) || TIME_RANGES[2]).hours;
    const since = new Date(Date.now() - hours * 3600_000).toISOString();
    try {
      const [profileR, histR, auditR, trendsR] = await Promise.all([
        getTrustProfile(selectedAgentId).catch(() => null),
        getAutonomyHistory(selectedAgentId, 0, 200).catch(() => null),
        getPolicyAuditTrail({ agentId: selectedAgentId, hours, page: 0, size: 500 }).catch(() => null),
        getAgentScoreTrends(selectedAgentId, Math.ceil(hours / 24)).catch(() => null),
      ]);
      setProfile(profileR?.data || profileR || null);

      /* G327 — Robust response normalisation. apiFetch wraps every
       * response as { ok, data }, and Spring Pageable endpoints
       * (/trust/profiles/{id}/history, /policy/audit/...) further
       * wrap as { content: [...], pageable: {...}, totalElements }.
       * Previously V2 stopped at r.data and didn't drill into
       * r.data.content — so any Pageable response silently
       * coerced to empty. */
      const histArr = extractList(histR);
      setTransitions(histArr.map((h) => ({ ...h, _kind: 'transition' })));

      const auditArr = extractList(auditR);
      setEnforcements(auditArr.map((e) => ({ ...e, _kind: 'enforcement' })));

      const trendArr = extractList(trendsR);
      setScorePoints(trendArr
        .map((p) => ({ score: Number(p.score), timestamp: p.timestamp || p.at || p.recordedAt }))
        .filter((p) => Number.isFinite(p.score) && p.timestamp));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [selectedAgentId, timeRange]);
  useEffect(() => { pg.setPage(0); /* eslint-disable-next-line */ }, [eventType, decision]);

  /* ── Merge + filter timeline ────────────────────────────────── */
  const merged = useMemo(() => {
    let list = [...transitions, ...enforcements];
    if (eventType === 'transition')  list = list.filter((e) => e._kind === 'transition');
    if (eventType === 'enforcement') list = list.filter((e) => e._kind === 'enforcement');
    if (decision !== 'all') {
      list = list.filter((e) => {
        if (e._kind !== 'enforcement') return false;
        const d = (e.decision || e.outcome || '').toUpperCase();
        return d === decision;
      });
    }
    list.sort((a, b) =>
      new Date(b.timestamp || b.at || b.createdAt || 0).getTime()
      - new Date(a.timestamp || a.at || a.createdAt || 0).getTime(),
    );
    return list;
  }, [transitions, enforcements, eventType, decision]);

  const visibleEvents = pg.paginate(merged);

  /* ── Stats summary ──────────────────────────────────────────── */
  const stats = useMemo(() => {
    const total = merged.length;
    const tCount = merged.filter((e) => e._kind === 'transition').length;
    const eCount = merged.filter((e) => e._kind === 'enforcement').length;
    const blocked = merged.filter((e) =>
      e._kind === 'enforcement' && (e.decision || e.outcome || '').toUpperCase() === 'BLOCKED').length;
    const warned = merged.filter((e) =>
      e._kind === 'enforcement' && (e.decision || e.outcome || '').toUpperCase() === 'WARNED').length;
    const allowed = merged.filter((e) =>
      e._kind === 'enforcement' && (e.decision || e.outcome || '').toUpperCase() === 'ALLOWED').length;
    return { total, transitions: tCount, enforcements: eCount, blocked, warned, allowed };
  }, [merged]);

  const selectedAgent = enriched.find((a) => a.id === selectedAgentId);

  /* Audit hash chain verifier — wired to a placeholder verify
   * function. The backend exposes /audit/verify-integrity (G222);
   * we call it via the policy audit trail's verifyIntegrity helper
   * if available, otherwise fall back to a synthetic check that
   * inspects event hashes for chain consistency.
   *
   * G330 — Backend `/audit/verify-integrity` expects `?hours=<int>`.
   * V2's local time range state holds the *key* string ("7d", "30d")
   * so we must convert via TIME_RANGES before dispatch. Prior code
   * passed the raw key, causing the backend to reject the request
   * with "Path or query parameter 'hours' could not be parsed as
   * int." and the AuditHashIntegrity widget to surface the verbatim
   * error to the operator. */
  const verifyIntegrity = async (arg) => {
    // P0-12 Day 3 — AuditHashIntegrity now hands us a structured config
    // ({ hours, agentId, timeRange }) so verification can scope to a
    // single agent. Accept both shapes so old call sites (passing a
    // bare range key) still work.
    const cfg = typeof arg === 'object' && arg !== null ? arg : { timeRange: arg };
    const range = cfg.timeRange;
    const matched = TIME_RANGES.find((r) => r.key === range)
                 || TIME_RANGES.find((r) => r.key === '30d');
    const hours = cfg.hours ?? matched?.hours ?? 720;
    const agentId = cfg.agentId;
    try {
      // Try the dedicated endpoint if available in api.js
      const api = await import('../../../../services/api');
      if (typeof api.verifyAuditIntegrity === 'function') {
        return api.verifyAuditIntegrity(agentId ? { hours, agentId } : hours);
      }
    } catch (_) { /* not in api.js — fall through */ }
    /* Synthetic verification — walk the loaded enforcement events and
     * confirm each event's `previousHash` matches the prior event's
     * `eventHash`. Surfaces tampering when the backend hasn't shipped
     * the dedicated verify endpoint yet. */
    const events = enforcements
      .filter((e) => e.eventHash)
      .sort((a, b) => new Date(a.timestamp || a.at).getTime() - new Date(b.timestamp || b.at).getTime());
    if (events.length < 2) return { data: { valid: true, eventCount: events.length } };
    for (let i = 1; i < events.length; i += 1) {
      if (events[i].previousHash && events[i].previousHash !== events[i - 1].eventHash) {
        return { data: { valid: false, eventCount: events.length, brokenAt: events[i].eventHash || `event ${i}` } };
      }
    }
    return { data: { valid: true, eventCount: events.length } };
  };

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">
      {/* Filter bar */}
      <Card padding="md">
        <div className="grid gap-3" style={{ gridTemplateColumns: '1.5fr 1fr 1fr 1fr auto' }}>
          <label className="block">
            <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
              Agent
            </span>
            <select
              value={selectedAgentId}
              onChange={(e) => { setSelectedAgentId(e.target.value); onFocusAgent?.(e.target.value); }}
              className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none"
            >
              {enriched.length === 0 && <option value="">No agents in scope</option>}
              {enriched.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} — score {a.trust ?? '—'} {a.probationUntil ? '· PROBATION' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
              Time range
            </span>
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}
                    className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none">
              {TIME_RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
              Event type
            </span>
            <select value={eventType} onChange={(e) => setEventType(e.target.value)}
                    className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none">
              {EVENT_TYPES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
              Decision
            </span>
            <select value={decision} onChange={(e) => setDecision(e.target.value)}
                    className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none">
              {DECISIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </label>
          <Button variant="ghost" size="sm"
                  iconLeft={<RefreshCw size={13} className={loading ? 'animate-spin' : ''} />}
                  onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>
      </Card>

      {/* Probation tracker */}
      {profile?.probationUntil && (
        <ProbationTracker
          startedAt={profile.probationStartedAt || profile.probationStart || profile.lastTransitionAt}
          expiresAt={profile.probationUntil}
          reason={profile.probationReason}
          currentScore={profile.complianceScore ?? selectedAgent?.trust}
          recoveryThreshold={profile.probationRecoveryThreshold || 75}
        />
      )}

      {/* Score waterfall */}
      {scorePoints.length >= 2 ? (
        <ScoreWaterfall points={scorePoints} />
      ) : (
        <Card padding="md">
          <p className="text-[12.5px] text-v2-ink-muted m-0 italic">
            Not enough score history in this window to render a waterfall (need at least 2 datapoints).
          </p>
        </Card>
      )}

      {/* G328 — Horizontal Timeline Scrubber. Restored after audit
          confirmed it was completely missing from V2 (the V1
          TimelineScrubber.jsx component wasn't imported). Sits
          between the waterfall (continuous score) and the stats
          strip (event totals) so the operator can scrub through
          discrete events visually before drilling into the
          paginated vertical timeline below.

          Wired to the same `merged` feed so the scrubber and the
          vertical timeline stay in sync — filtering by event type
          or decision in the filter bar updates both. */}
      <Card padding="md">
        <Card.Header
          title="Interactive timeline"
          subtitle="Scrub or use ← → keys to inspect individual events. Markers are shape-coded: ◆ block, ▲ warn, ★ transition, ● allow."
        />
        <Card.Body>
          <TimelineScrubber
            events={merged}
            agentName={selectedAgent?.name}
            agentCreatedAt={selectedAgent?.createdAt || profile?.createdAt}
          />
        </Card.Body>
      </Card>

      {/* Stats summary strip */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        <StatCard label="Total events"   value={stats.total}        tone="default" />
        <StatCard label="Transitions"    value={stats.transitions}  tone="cyan" />
        <StatCard label="Enforcements"   value={stats.enforcements} tone="action" />
        <StatCard label="Blocked"        value={stats.blocked}      tone="danger" />
        <StatCard label="Warned"         value={stats.warned}       tone="warning" />
        <StatCard label="Allowed"        value={stats.allowed}      tone="success" />
      </div>

      {/* Audit hash integrity + P0-12 Sprint 2 witness pill */}
      <AuditHashIntegrity
        verifyFn={verifyIntegrity}
        witnessFn={getLatestAuditWitness}
        timeRange={timeRange}
      />

      {/* Vertical timeline */}
      <Card padding="md">
        <Card.Header
          title="Timeline"
          subtitle={`${merged.length} event${merged.length === 1 ? '' : 's'} in window · oldest at bottom`}
        />
        <Card.Body>
          {loading && <p className="text-v2-ink-muted text-[12.5px]">Loading events…</p>}
          {!loading && merged.length === 0 && (
            <EmptyState
              icon={<Activity size={20} />}
              title="No events recorded"
              description="No autonomy transitions or policy enforcements in this window. Try widening the time range or clearing filters."
            />
          )}
          {!loading && merged.length > 0 && (
            <>
              <ol className="relative m-0 p-0 list-none space-y-3">
                {/* The vertical guide rail */}
                <div className="absolute left-[10px] top-0 bottom-0 w-px bg-v2-border-subtle" aria-hidden />
                {visibleEvents.map((e, i) => (
                  <TimelineEvent key={`${e._kind}-${e.id || i}-${e.timestamp || ''}`} event={e} />
                ))}
              </ol>

              <div className="mt-4">
                <Pagination
                  totalItems={merged.length}
                  page={pg.page}
                  pageSize={pg.pageSize}
                  onPageChange={pg.setPage}
                  onPageSizeChange={pg.setPageSize}
                  noun="events"
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

/* ───── Helpers ───── */

function StatCard({ label, value, tone }) {
  const TONE = {
    default: 'border-v2-border',
    cyan:    'border-v2-accent-cyan',
    action:  'border-v2-action',
    danger:  'border-v2-danger',
    warning: 'border-v2-warning',
    success: 'border-v2-success',
  };
  return (
    <div className={`bg-v2-surface border-t-2 ${TONE[tone] || TONE.default} border-x border-b border-v2-border-subtle rounded-v2-sm p-3 text-center`}>
      <div className="text-[22px] font-bold text-v2-ink">{value.toLocaleString()}</div>
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mt-0.5">
        {label}
      </div>
    </div>
  );
}

function TimelineEvent({ event }) {
  const [expanded, setExpanded] = useState(false);
  const isTransition = event._kind === 'transition';
  const decision = (event.decision || event.outcome || '').toUpperCase();

  /* Visual classification — icon + tone per event type. */
  const config = isTransition
    ? {
      icon: ArrowUpRight, // overridden below per direction
      tone: 'cyan',
      label: 'Autonomy transition',
    }
    : decision === 'BLOCKED' ? { icon: XCircle,        tone: 'danger',  label: 'Blocked' }
    : decision === 'WARNED'  ? { icon: AlertTriangle,  tone: 'warning', label: 'Warned' }
    : decision === 'ALLOWED' ? { icon: CheckCircle2,   tone: 'success', label: 'Allowed' }
    :                          { icon: FileText,       tone: 'default', label: decision || 'Event' };

  const time = event.timestamp || event.at || event.createdAt;
  const fromLevel = event.previousLevel || event.fromLevel || event.fromAutonomy;
  const toLevel = event.newLevel || event.toLevel || event.toAutonomy || event.autonomyLevel;
  const direction = (() => {
    const rank = { SELF_AUTONOMOUS: 4, SUPERVISED: 3, RESTRICTED: 2, HUMAN_CONTROLLED: 1, PROBATION: 0 };
    if (rank[toLevel] == null || rank[fromLevel] == null) return 0;
    return rank[toLevel] - rank[fromLevel];
  })();

  const TONE_BG = {
    cyan:    'bg-v2-accent-cyan-soft text-v2-accent-cyan-strong',
    action:  'bg-v2-action-soft text-v2-action-strong',
    danger:  'bg-v2-danger-soft text-v2-danger-strong',
    warning: 'bg-v2-warning-soft text-v2-warning-strong',
    success: 'bg-v2-success-soft text-v2-success-strong',
    default: 'bg-v2-surface-muted text-v2-ink-secondary',
  };

  return (
    <li className="pl-8 relative">
      {/* Dot on the rail */}
      <span className={`absolute left-0 top-1 w-5 h-5 rounded-full flex items-center justify-center ${TONE_BG[config.tone]}`}>
        {isTransition
          ? (direction >= 0
              ? <ArrowUpRight size={11} />
              : <ArrowDownRight size={11} />)
          : <config.icon size={11} />}
      </span>

      {/* Card */}
      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md overflow-hidden">
        <button onClick={() => setExpanded((v) => !v)}
                className="w-full px-3 py-2.5 text-left hover:bg-v2-surface-muted/50 flex items-center gap-3">
          <span className="text-[12.5px] font-semibold text-v2-ink shrink-0">{config.label}</span>
          {isTransition && (
            <>
              <Pill tone="muted" size="sm">{(fromLevel || '—').replace(/_/g, ' ')}</Pill>
              <span className="text-v2-ink-muted">→</span>
              <Pill tone={direction > 0 ? 'success' : direction < 0 ? 'danger' : 'muted'} size="sm">
                {(toLevel || '—').replace(/_/g, ' ')}
              </Pill>
            </>
          )}
          {!isTransition && (
            <>
              <Pill tone={config.tone === 'default' ? 'muted' : config.tone} size="sm" dot>{decision || 'event'}</Pill>
              {event.skillName && <span className="text-[12px] text-v2-ink-secondary italic truncate">{event.skillName}</span>}
            </>
          )}
          <span className="ml-auto font-mono-v2 text-[11px] text-v2-ink-muted">
            {time ? new Date(time).toLocaleString() : '—'}
          </span>
          <ChevronDown size={13} className={`text-v2-ink-muted transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {expanded && (
          <div className="px-3 pb-3 pt-1 border-t border-v2-border-subtle bg-v2-surface-muted/30">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[11.5px] mb-3">
              {isTransition && (
                <>
                  <Field label="Trigger type" value={event.triggerType || event.trigger || 'auto'} />
                  <Field label="Trigger reason" value={event.triggerReason || event.reason} />
                  <Field label="Score at transition" value={event.scoreAtTransition ?? event.trustScore} />
                  <Field label="Compliance score" value={event.complianceScore} />
                  <Field label="Violation summary" value={event.violationSummary} />
                  <Field label="Decided by" value={event.decidedBy || event.actorEmail || event.user} />
                  <Field label="Probation until" value={event.probationUntil ? new Date(event.probationUntil).toLocaleString() : null} />
                  <Field label="Timestamp" value={time ? new Date(time).toLocaleString() : null} />
                </>
              )}
              {!isTransition && (
                <>
                  <Field label="Event type" value={event.eventType || 'POLICY_RULE_EVALUATION'} />
                  <Field label="Decision" value={decision} />
                  <Field label="Skill" value={event.skillName} />
                  <Field label="Skill ID" value={event.skillId} mono />
                  <Field label="Rule ID" value={event.ruleId} mono />
                  <Field label="Rule version" value={event.ruleVersion} />
                  <Field label="Evaluation time" value={event.evaluationTimeMs != null ? `${event.evaluationTimeMs} ms` : null} />
                  <Field label="Workflow ID" value={event.workflowId} mono />
                  <Field label="Token ID" value={event.tokenId} mono />
                  <Field label="Timestamp" value={time ? new Date(time).toLocaleString() : null} />
                </>
              )}
            </dl>

            {/* Audit hash chain */}
            {(event.eventHash || event.previousHash) && (
              <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-sm p-2.5 mb-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1.5">
                  Audit hash chain
                </div>
                <div className="space-y-1 text-[11px] font-mono-v2">
                  {event.eventHash && (
                    <div className="flex items-center gap-2">
                      <span className="text-v2-ink-muted shrink-0">event</span>
                      <code className="text-v2-action-strong truncate">{String(event.eventHash).slice(0, 16)}…</code>
                      <CheckCircle2 size={11} className="text-v2-success-strong shrink-0" />
                    </div>
                  )}
                  {event.previousHash && (
                    <div className="flex items-center gap-2">
                      <span className="text-v2-ink-muted shrink-0">prev </span>
                      <code className="text-v2-ink-muted truncate">{String(event.previousHash).slice(0, 16)}…</code>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Execution detail JSON */}
            {event.executionDetail && (
              <details className="text-[11px]">
                <summary className="cursor-pointer text-v2-ink-secondary hover:text-v2-ink font-semibold uppercase tracking-wider text-[10px]">
                  Execution details
                </summary>
                <pre className="mt-2 bg-v2-ink/95 text-v2-surface rounded-v2-sm p-3 font-mono-v2 text-[10.5px] overflow-auto max-h-[200px]"
                     style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {typeof event.executionDetail === 'string'
                    ? event.executionDetail
                    : JSON.stringify(event.executionDetail, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function Field({ label, value, mono }) {
  if (value == null || value === '') return null;
  return (
    <>
      <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider">{label}</dt>
      <dd className={`text-v2-ink m-0 break-all ${mono ? 'font-mono-v2 text-[10.5px]' : ''}`}>{String(value)}</dd>
    </>
  );
}

export default TrustTimelineTab;
